import { useCallback, useMemo, useState } from "react";

import type { AxiosInstance } from "axios";

import { toastError, toastSuccess } from "@alpacahq/ui/old";

import { getErrorMessage } from "~/v2/api/hooks";
import { useMutationRequest } from "~/v2/api/hooks/useMutationRequest";
import {
  type CryptoAsset,
  type CryptoWhitelistedAddress,
  deleteWhitelistedAddress,
} from "~/v2/api/rest/cryptoWallet";
import {
  deriveCustodian,
  deriveWalletOwner,
  getChainIconKey,
  resolveAssetChainCell,
} from "../util";
import type { WhitelistDetailsModalDetails } from "./presentation/WhitelistDetailsModal";

interface UseAddressDetailsProps {
  cryptoWalletAxiosInstance: AxiosInstance;
  accountId: string;
  refetchWhitelist: () => void;
  assetMap: Record<string, CryptoAsset>;
}

export interface UseAddressDetailsReturn {
  onDetailsClicked: (address: CryptoWhitelistedAddress) => void;
  detailsModal: {
    details: WhitelistDetailsModalDetails | null;
    onClose: () => void;
    onDelete: () => void;
    isDeleteLoading: boolean;
  };
}

export const useAddressDetails = ({
  cryptoWalletAxiosInstance,
  accountId,
  refetchWhitelist,
  assetMap,
}: UseAddressDetailsProps): UseAddressDetailsReturn => {
  const [address, setAddress] = useState<CryptoWhitelistedAddress | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const onClose = useCallback(() => {
    setIsModalOpen(false);
    setAddress(null);
  }, []);

  const deleteWhitelistAddressMutation = useMutationRequest(
    async (whitelistId: string) =>
      deleteWhitelistedAddress(
        cryptoWalletAxiosInstance,
        accountId,
        whitelistId,
      ),
    {
      onSuccess: () => {
        toastSuccess("Whitelist address removed");
        onClose();
        refetchWhitelist();
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        toastError(`Failed to delete whitelist address: ${errorMessage}`);
      },
    },
  );

  const onDetailsClicked = useCallback((address: CryptoWhitelistedAddress) => {
    setAddress(address);
    setIsModalOpen(true);
  }, []);

  const onDelete = useCallback(() => {
    if (address) {
      deleteWhitelistAddressMutation.mutate(address.id);
    }
  }, [address, deleteWhitelistAddressMutation]);

  const details = useMemo<WhitelistDetailsModalDetails | null>(() => {
    if (!isModalOpen || !address) {
      return null;
    }

    const {
      chainLabel,
      assetLabel,
      iconKey: assetIconKey,
    } = resolveAssetChainCell({
      asset: address.asset,
      chain: address.chain,
      assetMap,
    });
    const isSelfHosted = address.travel_rule_info?.beneficiary_is_self_hosted;

    return {
      chainLabel,
      chainIconKey: getChainIconKey(address.chain),
      assetLabel,
      assetIconKey,
      address: address.address,
      selfHosted:
        isSelfHosted === undefined || isSelfHosted === null
          ? null
          : isSelfHosted
            ? "Yes"
            : "No",
      walletOwner: deriveWalletOwner(address),
      custodian: deriveCustodian(address),
    };
  }, [address, isModalOpen, assetMap]);

  return {
    onDetailsClicked,
    detailsModal: {
      details,
      onClose,
      onDelete,
      isDeleteLoading: deleteWhitelistAddressMutation.isPending,
    },
  };
};
